import { useState } from 'react';
import { X } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { useClaudeConnection } from '../lib/claudeConnection';
import { ClaudeConnectFlow } from './ClaudeConnectFlow';

/**
 * Invitation à connecter son compte Claude, à l'ouverture de l'application.
 *
 * Sans jeton d'abonnement, aucune proposition ne peut être générée. Le dire à
 * l'entrée vaut mieux que de le laisser découvrir au moment de générer, quand
 * l'avant-vente est déjà ouverte et la présentation attendue.
 *
 * La connexion se fait ici même, et non par un renvoi vers l'écran Admin : une
 * invitation qui déplace le problème ailleurs n'est qu'un avertissement de
 * plus. Chacun a son jeton, la question se pose donc à chaque utilisateur.
 */

/**
 * Invitation écartée pour cet onglet.
 *
 * `sessionStorage` et non `localStorage` : elle doit revenir à la prochaine
 * session tant que la connexion reste à faire, mais pas à chaque navigation
 * dans celle-ci.
 */
const CLE_ECARTEE = 'clippit.claude-connect.ecartee';

export function ClaudeConnectPrompt() {
    const { user } = useAuth();
    const [ecartee, setEcartee] = useState(
        () => sessionStorage.getItem(CLE_ECARTEE) === '1',
    );
    const [connecte, setConnecte] = useState(false);
    // Enregistré ne suffit pas : le serveur essaie le jeton dans la foulée, et
    // c'est ce résultat qui décide du message. Annoncer « connecté » sur un
    // jeton refusé reproduirait exactement la panne qu'on vient de corriger.
    const connection = useClaudeConnection(() => setConnecte(true));
    const reussi = connecte && connection.verification?.ok !== false;

    // `undefined` quand le serveur ne dit rien, par exemple sur une session
    // ouverte avant cette version : on n'invite que sur un « non » explicite.
    if (user?.claudeConnected !== false || ecartee) return null;

    function ecarter() {
        sessionStorage.setItem(CLE_ECARTEE, '1');
        setEcartee(true);
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div
                className="card w-full max-w-lg max-h-[90vh] overflow-y-auto"
                role="dialog"
                aria-modal="true"
                aria-labelledby="claude-connect-titre"
            >
                <div className="mb-4 flex items-start justify-between gap-4">
                    <h2
                        id="claude-connect-titre"
                        className="text-lg font-semibold text-[var(--color-text)]"
                    >
                        {reussi ? 'Compte Claude connecté' : 'Connectez votre compte Claude'}
                    </h2>
                    <button
                        onClick={ecarter}
                        className="rounded-lg p-1 hover:bg-[var(--color-bg-hover)]"
                        aria-label="Fermer"
                    >
                        <X className="h-4 w-4 text-[var(--color-text-secondary)]" />
                    </button>
                </div>

                {reussi ? (
                    <>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                            Votre jeton est enregistré. La génération de propositions est
                            disponible depuis n'importe quelle avant-ventes.
                        </p>
                        <div className="mt-4 flex justify-end">
                            <button className="btn-primary" onClick={ecarter}>Terminer</button>
                        </div>
                    </>
                ) : (
                    <div className="space-y-3">
                        <p className="text-sm text-[var(--color-text-secondary)]">
                            La génération de propositions commerciales passe par votre
                            abonnement Claude. Tant que votre compte n'est pas connecté, elle
                            reste indisponible. La connexion tient en un clic et vaut un an.
                        </p>
                        <p className="text-xs text-[var(--color-text-muted)]">
                            Le jeton est le vôtre : vos générations consomment votre
                            abonnement, et personne d'autre ne le voit ni ne l'utilise.
                        </p>

                        {connection.error && (
                            <p className="rounded-lg border border-[var(--color-warning)] px-3 py-2 text-xs text-[var(--color-warning)]">
                                {connection.error}
                            </p>
                        )}

                        <ClaudeConnectFlow
                            connection={connection}
                            libelleDemarrage="Connecter mon compte"
                            onCancel={ecarter}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
